/**
 * EntraService
 * 
 * Service responsible for handling Microsoft Entra ID (Azure AD) authentication and
 * user management throughout the MacSafety web application. Manages user login/logout,
 * token acquisition, and user state management.
 * 
 * Key Features:
 * - MSAL (Microsoft Authentication Library) integration
 * - User authentication and session management
 * - Access token acquisition and management
 * - User permissions and admin role checking
 * - User state management via Angular signals
 * - Automatic token refresh and queuing
 * - Session validation and testing via API endpoints
 * 
 * Components that utilize this service:
 * - All components requiring user authentication status
 * - Components displaying user information (username, email)
 * - Components checking user permissions for UI access/visibility
 * - Guards for route protection
 * - Components requiring API authentication tokens
 * - Any component that needs to verify if a user is logged in
 * - Form components requiring session validation before user interaction

 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Inject, Injectable, signal, computed, OnDestroy } from '@angular/core';
import { Observable, from, of, tap, switchMap, Subject, filter, takeUntil, EMPTY } from 'rxjs';
import { catchError, map, finalize, share } from 'rxjs/operators';
import { ToastService } from './toast.service';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  AccountInfo,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';

import { environment } from '../../environments/environment';
import { User } from '../models/User.interface';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class EntraService implements OnDestroy {
  // #region Signals
  // Private signal for storing user information (name, email, admin status)
  private readonly _user = signal<User>({
    email: '',
    name: '',
    isAdmin: false,
    hasPfizerFormAccess: false,
    canEditTemplates: false
  });

  // Private signal for tracking user login status
  private readonly _userIsLoggedIn = signal<boolean>(false);

  // Public readonly signals for components to access user state
  public readonly user = this._user.asReadonly();
  public readonly userIsLoggedIn = this._userIsLoggedIn.asReadonly();

  // Computed signals for derived user state information
  public readonly userName = computed(() => this._user().name);
  public readonly userEmail = computed(() => this._user().email);
  public readonly isAdmin = computed(() => this._user().isAdmin);
  public readonly hasPfizerFormAccess = computed(() => this._user().hasPfizerFormAccess);
  public readonly canEditTemplates = computed(() => this._user().canEditTemplates);
  public readonly userFirstName = computed(() => this._user().name.split(' ')[0]);
  public readonly userLastName = computed(() => this._user().name.split(' ')[1]);
  // #endregion

  // #region Private Properties
  // Current access token for API authentication
  private _accessToken?: string;
  
  // Token expiration timestamp (Unix timestamp in seconds)
  private _tokenExpiration = 0;
  
  // Subject for managing component destruction and unsubscribing
  private readonly _destroying$ = new Subject<void>();
  
  // Observable for queuing token requests to prevent multiple simultaneous requests
  private tokenRequest$?: Observable<string | undefined>;
  
  // Flag to prevent multiple simultaneous setUser calls
  private _isSettingUser = false;

  private apiUrl: string;
  // #endregion

  // #region Public Properties
  /**
   * Gets the current token expiration timestamp
   * @returns Unix timestamp when the current token expires
   */
  public get tokenExpiration(): number {
    return this._tokenExpiration;
  }
  // #endregion

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly _toastService: ToastService,
    private readonly _httpClient: HttpClient
    ) {
      this.apiUrl = environment.apiBaseUrl;
    }

  // #region Lifecycle
  /**
   * Cleanup method called when service is destroyed
   * Completes the destruction subject to unsubscribe from all observables
   */
  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
  // #endregion

  // #region Initialization
  /**
   * Initializes the MSAL instance and sets up authentication state
   * Called by APP_INITIALIZER in app.module.ts during application startup
   * Sets up MSAL, handles redirect responses, and configures broadcast subscriptions
   */
  public async msalInstanceInit(): Promise<void> {
    try {
      await this.initializeMsalInstance();
      await this.handleRedirectResponse();
      this.checkExistingAccounts();
      this.setupBroadcastSubscription();
      
    } catch (error) {
      console.error('Failed to initialize MSAL instance:', error);
      throw error;
    }
  }

  /**
   * Initializes the MSAL instance
   * @returns Promise that resolves when MSAL is initialized
   */
  private async initializeMsalInstance(): Promise<void> {
    await this.msalService.instance.initialize();
  }

  /**
   * Handles redirect response from authentication flow
   * Processes the response and sets the active account if authentication was successful
   */
  private async handleRedirectResponse(): Promise<void> {
    const response = await this.msalService.instance.handleRedirectPromise();
    if (response?.account) {
      this.msalService.instance.setActiveAccount(response.account);
      this.setAuthenticatedUser(response.account);
    }
  }

  /**
   * Checks for existing authenticated accounts
   * Sets the first available account as active if any exist
   */
  private checkExistingAccounts(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      this.msalService.instance.setActiveAccount(account);
      this.setAuthenticatedUser(account);
    }
  }

  /**
   * Sets up subscription to MSAL broadcast events
   * Monitors authentication state changes and updates user status accordingly
   * Prevents multiple simultaneous user setup calls
   */
  private setupBroadcastSubscription(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        
        // Only update login status, don't call setUser again
        const accounts = this.msalService.instance.getAllAccounts();
        const loggedIn = accounts.length > 0;
        
        // Only update if the status actually changed
        if (this._userIsLoggedIn() !== loggedIn) {
          this._userIsLoggedIn.set(loggedIn);
          
          // Only set user if we transitioned from not logged in to logged in
          if (loggedIn && !this._user().email) {
            const account = this.msalService.instance.getActiveAccount() || accounts[0];
            if (account) {
              this.msalService.instance.setActiveAccount(account);
              // Don't call setUser here - it's already been called during initialization
            }
          }
        }
      });
  }

  /**
   * Displays error message when user session requires refresh
   * Called when API requests return 401/403 status codes
   */
  public sessionRefreshRequiredError(): void {
    this._toastService.showErrorMessage('Your session has expired or requires a refresh. Please log out and log back in to continue.');
  }

  // Remove this method since we're handling it inline in setupBroadcastSubscription
  // private handleInteractionComplete(): void {
  //   const accounts = this.msalService.instance.getAllAccounts();
  //   const loggedIn = accounts.length > 0;
  //   this._userIsLoggedIn.set(loggedIn);

  //   if (loggedIn) {
  //     const account = this.msalService.instance.getActiveAccount() || accounts[0];
  //     if (account) {
  //       this.msalService.instance.setActiveAccount(account);
  //       this.setUser(account);
  //     }
  //   }
  // }
  // #endregion

  // #region Authentication
  /**
   * Initiates the login process using MSAL redirect flow
   * Redirects user to Microsoft Entra ID for authentication
   */
  public login(): void {
    const authRequest = this.msalGuardConfig.authRequest 
      ? { ...this.msalGuardConfig.authRequest } as RedirectRequest
      : undefined;
      
    this.msalService.loginRedirect(authRequest);
  }

  /**
   * Logs out the current user and clears all session data
   * Redirects user to Microsoft Entra ID logout page
   */
  public logout(): void {
    this.clearUserState();
    this.msalService.logoutRedirect();
  }

  /**
   * Clears all user state and session data
   * Resets user information, tokens, and login status
   */
  private clearUserState(): void {
    this._userIsLoggedIn.set(false);
    this._user.set({ email: '', name: '', isAdmin: false, hasPfizerFormAccess: false, canEditTemplates: false });
    this._accessToken = undefined;
    this._tokenExpiration = 0;
  }
  // #endregion

  // #region User Management
  /**
   * Sets the authenticated user and initiates token acquisition
   * Called when a user successfully authenticates
   * 
   * @param account - The MSAL account information for the authenticated user
   */
  private setAuthenticatedUser(account: AccountInfo): void {
    this._userIsLoggedIn.set(true);
    this.setUser(account);
  }

  /**
   * Processes user account information and sets user state
   * Extracts user details, checks permissions, and acquires initial token
   * Implements protection against multiple simultaneous calls
   * 
   * @param account - The MSAL account information to process
   */
  public async setUser(account: AccountInfo): Promise<void> {
    // Prevent multiple simultaneous setUser calls
    if (this._isSettingUser) {
      return;
    }

    this._isSettingUser = true;
    
    if (environment.testing){
      console.log('My user account:', account)
    }


    try {
      const userAccess = this.processUserAccess(account);
      const userInfo = this.processUserInfo(account);
      
      this._user.set({
        name: userInfo.name,
        email: userInfo.email,
        isAdmin: userAccess.isAdmin,
        hasPfizerFormAccess: userAccess.hasPfizerFormAccess,
        canEditTemplates: userAccess.canEditTemplates
      });
      // if (environment.useTestUser) {
      // this._user.set({
      //   name: 'Test User',
      //   email: 'test@test.com',
      //   isAdmin: true,
      //   hasPfizerFormAccess: true,
      //   canEditTemplates: false
      // });
      // }

      console.log('User set:', this._user());

      if (userAccess.hasAccess) {
        await this.acquireInitialToken(account);
      }
    } finally {
      this._isSettingUser = false;
    }
  }

  /**
   * Processes user access permissions from account claims
   * Checks group membership for admin role and determines access level
   * 
   * @param account - The MSAL account to check permissions for
   * @returns Object containing access status and admin role information
   */
  private processUserAccess(account: AccountInfo): { hasAccess: boolean; isAdmin: boolean; hasPfizerFormAccess: boolean; canEditTemplates: boolean } {
    const groups: any = account.idTokenClaims?.['groups'] || [];
    
    // For now, assuming all users have access - adjust based on requirements
    const hasAccess = true;
    const isAdmin = groups.includes(environment.adAdminGroup);
    const canEditTemplates = groups.includes(environment.templateEditorsGroup);
    const hasPfizerFormAccess = groups.includes(environment.adPfizerFormAccessGroup);
    
    // Store access status for persistence
    localStorage.setItem('userHasAccess', JSON.stringify(hasAccess));
    
    return { hasAccess, isAdmin, hasPfizerFormAccess, canEditTemplates };
  }

  /**
   * Extracts and formats user information from account claims
   * Handles name formatting and email extraction
   * 
   * @param account - The MSAL account to extract user info from
   * @returns Object containing formatted name and email
   */
  private processUserInfo(account: AccountInfo): { name: string; email: string } {
    const name = this.formatUserName(account.name || '');
    const email = account.username || '';
    
    return { name, email };
  }

  /**
   * Formats user name from various input formats
   * Handles "Last, First" format and converts to "First Last"
   * 
   * @param rawName - The raw name string from account claims
   * @returns Formatted name string
   */
  private formatUserName(rawName: string): string {
    if (!rawName) return '';
    
    const parts = rawName.split(',').map(part => part.trim());
    
    // Handle "Last, First" format
    if (parts.length === 2) {
      const [last, first] = parts;
      return `${first} ${last}`;
    }
    
    return rawName;
  }

  /**
   * Acquires the initial access token for the authenticated user
   * Called after user setup to ensure API access is available
   * 
   * @param account - The account to acquire token for
   */
  private async acquireInitialToken(account: AccountInfo): Promise<void> {
    
    // Use the main acquireToken method which has queuing logic
    // Convert to promise to maintain async signature
    try {
      const accessToken = await this.acquireToken().toPromise();
      if (accessToken) {
        console.log('Initial token acquisition successful');
      }
    } catch (error) {
      console.error('Error acquiring initial token:', error);
    }
  }
  // #endregion

  // #region Token Management

  /**
   * Acquires access token from cache or silently requests new one
   * Implements queuing to prevent multiple simultaneous token requests
   * Returns cached token if valid, otherwise requests new token from Microsoft Entra ID
   * 
   * @returns Observable that emits the access token string or undefined if failed
   */
  public acquireToken(): Observable<string | undefined> {
    const requestId = Math.random().toString(36).substr(2, 6);
    
    // DEBUG: Show better stack trace to see what's calling this
    const stack = new Error().stack;
    const stackLines = stack ? stack.split('\n').slice(1, 6) : ['No stack available'];
    stackLines.forEach((line, index) => {
      console.log(`  ${index + 1}: ${line.trim()}`);
    });

    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      this.logout();
      return of(undefined);
    }

    if (this.hasValidCachedToken()) {
      return of(this._accessToken);
    }

    // If there's already a token request in progress, return that observable
    if (this.tokenRequest$) {
      return this.tokenRequest$;
    }

    
    this.tokenRequest$ = this.acquireTokenSilently(environment.apiScope).pipe(
      tap((accessToken) => {
        if (accessToken) {
          this.updateLocalToken(accessToken);
        }
      }),
      catchError((err) => {
        console.error(`[${requestId}] Error acquiring token:`, err);
        return of(undefined);
      }),
      finalize(() => {
        // Clear the request observable when complete (success or error)
        console.log(`[${requestId}] Token request completed, clearing queue`);
        this.tokenRequest$ = undefined;
      }),
      share() // Share the observable among multiple subscribers
    );

    return this.tokenRequest$;
  }

  /**
   * Silently acquires access token using MSAL
   * Attempts to get token without user interaction, using cached credentials
   * 
   * @param scopes - Array of permission scopes to request
   * @param account - Optional account to use (defaults to active account)
   * @returns Observable that emits the access token or undefined if failed
   */
  public acquireTokenSilently(
    scopes: string[],
    account?: AccountInfo
  ): Observable<string | undefined> {
    const requestId = Math.random().toString(36).substr(2, 6);
    const activeAccount = account || this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      console.error(`[${requestId}] No active account! Please log in.`);
      return of(undefined);
    }

    console.log(`[${requestId}] Acquiring token silently for account:`, activeAccount.username);

    return this.msalService
      .acquireTokenSilent({
        account: activeAccount,
        scopes: scopes,
      })
      .pipe(
        map((response) => {
          console.log(`[${requestId}] Token acquired silently successfully`);
          return response.accessToken;
        }),
        catchError((error) => {
          console.error(`[${requestId}] Error acquiring token silently`, error);
          return of(undefined);
        })
      );
  }

  /**
   * Checks if the currently cached token is still valid
   * @returns True if token exists and hasn't expired, false otherwise
   */
  private hasValidCachedToken(): boolean {
    return !!(this._accessToken && !this.isTokenExpired(this._tokenExpiration));
  }

  /**
   * Checks if a token has expired based on its expiration timestamp
   * @param tokenExpiration - Unix timestamp when token expires
   * @returns True if token is expired, false if still valid
   */
  private isTokenExpired(tokenExpiration: number): boolean {
    const currentTime = Math.floor(Date.now() / 1000);
    return tokenExpiration < currentTime;
  }

  /**
   * Updates the local token storage and extracts expiration information
   * Decodes the JWT token to get expiration timestamp
   * 
   * @param token - The new access token to store
   */
  private updateLocalToken(token: string): void {
    this._accessToken = token;
    try {
      const decoded: any = jwtDecode(token);
      this._tokenExpiration = decoded.exp;
    } catch (error) {
      console.error('Error decoding token:', error);
      this._tokenExpiration = 0;
    }
  }
  // #endregion

  // #region Utility Methods (Deprecated)
  /**
   * @deprecated This method is not currently being used
   * Checks if access token in AuthenticationResult has expired
   * 
   * @param authResult - The authentication result containing the token
   * @returns True if token is expired, false if still valid
   */
  public isAccessTokenExpired(authResult: AuthenticationResult): boolean {
    const token = authResult.accessToken;

    if (!token) {
      return true;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp;
      const currentTime = Math.floor(Date.now() / 1000);
      
      return currentTime >= expirationTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  }
  // #endregion



   /**
   * Validates the current user's authentication session and handles invalid sessions.
   * Shows warning message and automatically logs out user after 5-second delay if session is invalid.
   */
  validateSession(): void {
    this.testAuth().subscribe({
      next: (isValid: boolean) => {
        if (!isValid) {
          this._toastService.showWarningMessage(
            'Your login session has expired or is no longer valid. ' +
            'We need to log you out so you can sign in again. ' +
            'This will happen automatically in a few seconds.'
          , 5000);
          
          // Wait 5 seconds so user can read the message, then logout
          setTimeout(() => {
            this.logout();
          }, 5000);
        }
      }
    });
  }

  /**
   * Tests the validity of the current user's authentication session by making an API call.
   * 
   * This method is used by the SessionGuard to validate that the user's session is still active
   * and that they can successfully communicate with the backend API. It follows industry standards
   * for session validation by:
   * - Acquiring a fresh access token to ensure it hasn't expired
   * - Making an authenticated request to a protected endpoint
   * - Returning false if the request fails, indicating an expired or invalid session
   * 
   * @returns Observable<boolean> - true if session is valid, false if session has expired or is invalid
   * @throws Will emit false on any authentication or network errors
   */
  public testAuth(): Observable<boolean> {
    return from(this.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${this.apiUrl}/V1/auth-test`;
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });
  
        return this._httpClient.get<any>(url, { headers }).pipe(
          map(() => true),
          catchError((err) => {
            console.error('Auth test failed:', err);
            return of(false);
          })
        );
      })
    );
  }
  
}